import { Close as CloseIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { UserPlan } from '../../services/admin.service';

interface UserPlansModalProps {
    open: boolean;
    onClose: () => void;
    userName: string;
    userId: string;
    userPlans: UserPlan[];
    loading: boolean;
    error: string | null;
    onRefreshPlans: () => Promise<void>;
}

const UserPlansModal: React.FC<UserPlansModalProps> = ({
    open,
    onClose,
    userName,
    userId,
    userPlans,
    loading,
    error,
    onRefreshPlans,
}) => {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Group plans by type
    const taxReportPlans = userPlans.filter((plan) => plan.type === 'taxReport');
    const expertReviewPlans = userPlans.filter((plan) => plan.type === 'expertReview');
    // Note: "We Do Everything" addon has been removed
    const addonPlans = userPlans.filter((plan) => plan.type === 'addon');

    const getExpiryDate = (plan: UserPlan) => {
        return plan.expiryDate || plan.validUntil || 'N/A';
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="plans-dialog-title">
            <DialogTitle
                id="plans-dialog-title"
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Box>User Plans - {userName}</Box>
                <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                            <Tabs value={tabValue} onChange={handleTabChange} aria-label="plan type tabs">
                                <Tab label="Tax Reports" />
                                <Tab label="Expert Reviews" />
                                <Tab label="Add-ons" />
                            </Tabs>
                        </Box>

                        {/* Tax Reports Tab */}
                        {tabValue === 0 && (
                            <Paper elevation={0} variant="outlined" sx={{ mb: 2 }}>
                                <TableContainer>
                                    <Table size="medium">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Tax Year</TableCell>
                                                <TableCell>Package</TableCell>
                                                <TableCell>Transaction Limit</TableCell>
                                                <TableCell>Purchase Date</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {taxReportPlans.length > 0 ? (
                                                taxReportPlans.map((plan) => (
                                                    <TableRow key={plan.id}>
                                                        <TableCell>{plan.name}</TableCell>
                                                        <TableCell>{plan.taxYear || plan.year || 'N/A'}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.package || 'Standard'}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {plan.transactionLimit?.toLocaleString() || 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {new Date(plan.purchaseDate).toLocaleDateString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.status}
                                                                color={
                                                                    plan.status === 'active'
                                                                        ? 'success'
                                                                        : plan.status === 'expired'
                                                                          ? 'error'
                                                                          : 'warning'
                                                                }
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center">
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ py: 2 }}
                                                        >
                                                            No tax report plans found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                        {/* Expert Reviews Tab */}
                        {tabValue === 1 && (
                            <Paper elevation={0} variant="outlined" sx={{ mb: 2 }}>
                                <TableContainer>
                                    <Table size="medium">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Tax Year</TableCell>
                                                <TableCell>Purchase Date</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {expertReviewPlans.length > 0 ? (
                                                expertReviewPlans.map((plan) => (
                                                    <TableRow key={plan.id}>
                                                        <TableCell>{plan.name}</TableCell>
                                                        <TableCell>{plan.taxYear || plan.year || 'N/A'}</TableCell>
                                                        <TableCell>
                                                            {new Date(plan.purchaseDate).toLocaleDateString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.status}
                                                                color={
                                                                    plan.status === 'active'
                                                                        ? 'success'
                                                                        : plan.status === 'expired'
                                                                          ? 'error'
                                                                          : 'warning'
                                                                }
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ py: 2 }}
                                                        >
                                                            No expert review plans found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                        {/* Add-ons Tab */}
                        {tabValue === 2 && (
                            <Paper elevation={0} variant="outlined" sx={{ mb: 2 }}>
                                <TableContainer>
                                    <Table size="medium">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Add-on Type</TableCell>
                                                <TableCell>Purchase Date</TableCell>
                                                <TableCell>Valid Until</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {addonPlans.length > 0 ? (
                                                addonPlans.map((plan) => (
                                                    <TableRow key={plan.id}>
                                                        <TableCell>{plan.name}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.addonType || 'Standard'}
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {new Date(plan.purchaseDate).toLocaleDateString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            {getExpiryDate(plan) !== 'N/A'
                                                                ? new Date(getExpiryDate(plan)).toLocaleDateString()
                                                                : 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.status}
                                                                color={
                                                                    plan.status === 'active'
                                                                        ? 'success'
                                                                        : plan.status === 'expired'
                                                                          ? 'error'
                                                                          : 'warning'
                                                                }
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center">
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ py: 2 }}
                                                        >
                                                            No add-on plans found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UserPlansModal;
